/**
 * CountersignModal — sender signs their own fields after the recipient has signed.
 * Opens inside the authenticated app from the Signatures tab.
 */
import React, { useState, useEffect } from 'react';
import { X, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { esignAPI } from '@/lib/api';
import SignaturePad from '../sign/SignaturePad';

// ── PDF.js loader ─────────────────────────────────────────────────────────────

function loadPdfJs() {
  return new Promise((resolve) => {
    if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      resolve(window.pdfjsLib);
    };
    document.head.appendChild(s);
  });
}

const FIELD_CFG = {
  signature: { color: '#F97316', bg: '#FFF7ED', label: 'Sign here' },
  text:      { color: '#F97316', bg: '#FFF7ED', label: 'Enter text' },
  date:      { color: '#F97316', bg: '#FFF7ED', label: 'Date' },
};

// ── Signature pad ─────────────────────────────────────────────────────────────

// ── CountersignModal ──────────────────────────────────────────────────────────

export default function CountersignModal({ submissionId, onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [pages, setPages] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [fieldValues, setFieldValues] = useState({});
  const [activeSignField, setActiveSignField] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [executed, setExecuted] = useState(false);

  useEffect(() => {
    esignAPI.countersignView(submissionId)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => { toast.error('Failed to load'); onClose(); });
  }, [submissionId]);

  useEffect(() => {
    if (!data?.content_b64) return;
    let cancelled = false;
    setPdfLoading(true);
    (async () => {
      try {
        const pdfjsLib = await loadPdfJs();
        const bytes = Uint8Array.from(atob(data.content_b64), c => c.charCodeAt(0));
        const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
        const rendered = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.4 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width; canvas.height = viewport.height;
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          rendered.push({ canvas, pageNum: i });
        }
        if (!cancelled) { setPages(rendered); setPdfLoading(false); }
      } catch { if (!cancelled) setPdfLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [data]);

  const allFilled = (data?.fields || []).every(f => fieldValues[f.id]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const fieldValuesArr = Object.entries(fieldValues).map(([id, value]) => ({ id, value }));
      await esignAPI.countersign(submissionId, fieldValuesArr);
      setExecuted(true);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to execute');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
        <div>
          <h2 className="font-semibold text-[#1E2A33] text-base">Sign your part</h2>
          <p className="text-xs text-[#5C6670] mt-0.5">{data?.document_title}</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 text-[#5C6670]">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        {executed ? (
          <div className="flex flex-col items-center justify-center h-full text-center gap-3">
            <CheckCircle2 className="w-16 h-16 text-green-500" />
            <h3 className="text-xl font-semibold text-[#1E2A33]">Agreement fully executed</h3>
            <p className="text-[#5C6670]">Every party has completed their part. The document is now complete.</p>
            <Button onClick={onClose} className="mt-2 bg-[#A62B1F] hover:bg-[#e86d61] text-white">Close</Button>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-full gap-2 text-[#5C6670]">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="max-w-2xl mx-auto space-y-4">
            {/* Party info */}
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800 space-y-1">
              {(data.parties || []).map((p, i) => (
                <div key={p.id || i}>
                  <CheckCircle2 className="w-4 h-4 inline mr-1.5" />
                  <strong>{p.name || p.email}</strong> has {p.role === 'reviewer' ? 'reviewed' : 'signed'}.
                  {p.signed_at && ` Completed on ${new Date(p.signed_at).toLocaleDateString()}.`}
                </div>
              ))}
            </div>

            <p className="text-sm text-[#5C6670]">Now fill in your fields below to fully execute the agreement.</p>

            {/* PDF with sender field overlays */}
            {pdfLoading ? (
              <div className="text-center py-8 text-[#5C6670]">Rendering document…</div>
            ) : pages.length > 0 ? (
              <div className="space-y-4">
                {pages.map(pg => {
                  const pageFields = (data.fields || []).filter(f => f.page === pg.pageNum);
                  return (
                    <div key={pg.pageNum} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
                      <canvas
                        ref={el => { if (el) { el.width = pg.canvas.width; el.height = pg.canvas.height; el.getContext('2d').drawImage(pg.canvas, 0, 0); } }}
                        style={{ display: 'block', width: '100%', height: 'auto' }}
                      />
                      {pageFields.map(f => {
                        const cfg = FIELD_CFG[f.type] || FIELD_CFG.text;
                        const filled = !!fieldValues[f.id];
                        const isSig = f.type === 'signature';
                        return (
                          <div key={f.id}
                            onClick={() => isSig && setActiveSignField(f.id)}
                            style={{
                              position: 'absolute',
                              left: `${f.x_pct}%`, top: `${f.y_pct}%`,
                              width: `${f.w_pct || 28}%`, height: `${f.h_pct || 8}%`,
                              borderBottom: `2px solid ${filled ? '#9AA3AB' : cfg.color}`,
                              background: 'transparent',
                              display: 'flex', alignItems: 'flex-end',
                              justifyContent: isSig && !filled ? 'flex-start' : 'center',
                              overflow: 'visible',
                              cursor: isSig ? 'pointer' : 'default',
                            }}
                          >
                            {isSig && filled ? (
                              <img src={fieldValues[f.id]} alt="sig" style={{ maxWidth: '100%', maxHeight: '160%', objectFit: 'contain', paddingBottom: 2 }} />
                            ) : isSig ? (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                fontSize: 11, fontWeight: 700, color: '#fff',
                                background: cfg.color, padding: '3px 9px 3px 7px',
                                borderRadius: '3px 3px 3px 0', transform: 'translateY(2px)',
                                whiteSpace: 'nowrap', boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                              }}>✎ Sign here</span>
                            ) : (
                              <input type={f.type === 'date' ? 'date' : 'text'}
                                placeholder={cfg.label}
                                value={fieldValues[f.id] || ''}
                                onChange={e => setFieldValues(prev => ({ ...prev, [f.id]: e.target.value }))}
                                onClick={e => e.stopPropagation()}
                                style={{ width: '100%', height: '100%', border: 'none', background: 'transparent', textAlign: 'center', fontSize: 12, color: '#1E2A33', outline: 'none', padding: '0 4px' }}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ) : null}

            {/* Execute button */}
            <button
              onClick={handleSubmit}
              disabled={!allFilled || submitting}
              style={{
                width: '100%', padding: '13px 0', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 15,
                background: allFilled && !submitting ? '#F97316' : '#E3DDD1',
                color: allFilled && !submitting ? '#fff' : '#9AA3AB',
                cursor: allFilled && !submitting ? 'pointer' : 'not-allowed',
              }}
            >
              {submitting ? 'Executing…' : 'Execute Agreement'}
            </button>
            <p style={{ fontSize: 12, color: '#9AA3AB', textAlign: 'center' }}>
              By clicking "Execute Agreement" you confirm your counter-signature on this document.
            </p>
          </div>
        )}
      </div>

      {activeSignField && (
        <SignaturePad
          primaryColor="#F97316"
          onDone={dataUrl => { setFieldValues(prev => ({ ...prev, [activeSignField]: dataUrl })); setActiveSignField(null); }}
          onCancel={() => setActiveSignField(null)}
        />
      )}
    </div>
  );
}
